import React from 'react';
import {
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  useTheme,
  Box,
  Collapse,
  IconButton,
} from '@mui/material';
import { TimeSlot } from '../../../types/workingHours';
import { generateTimeOptions, addOneHourToTime } from '../../../utils/timeUtils';
import CustomSwitch from '../../common/CustomSwitch';
import { MdDelete } from 'react-icons/md';
import { IoIosAddCircleOutline } from 'react-icons/io';

interface DayTimeSlotProps {
  day: string;
  timeSlot: TimeSlot[];
  onChange: (day: string, index: number, updates: Partial<TimeSlot>) => void;
  onDeleteSlot: (day: string, index: number) => void;
  onToggleDay: (day: string, enabled: boolean) => void;
}

const DayTimeSlot: React.FC<DayTimeSlotProps> = ({
  day,
  timeSlot,
  onChange,
  onDeleteSlot,
  onToggleDay,
}) => {
  const theme = useTheme();
  const startTimeOptions = generateTimeOptions();

  const handleStartTimeChange = (index: number, value: string) => {
    onChange(day, index, {
      startTime: value,
      endTime: addOneHourToTime(value),
    });
  };

  const enabledTimeSlots = timeSlot.filter(slot => slot.enabled);
  const isDayEnabled = enabledTimeSlots.length > 0;

  const handleToggleDay = (enabled: boolean) => {
    onToggleDay(day, enabled);
    if (enabled) {
      onChange(day, 0, {
        startTime: '09:00',
        endTime: '17:00',
        enabled: true,
        isModified: false,
      });
    }
  };

  const handleAddSlot = () => {
    const lastSlot = timeSlot[timeSlot.length - 1];
    const newStartTime = lastSlot.endTime;
    const newEndTime = addOneHourToTime(newStartTime);

    onChange(day, timeSlot.length, {
      startTime: newStartTime,
      endTime: newEndTime,
      enabled: true,
      isModified: false,
    });
  };

  const handleDeleteSlot = (index: number) => {
    onDeleteSlot(day, index);
  };

  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 1,
        backgroundColor: theme.palette.background.paper,
        border: '1px solid',
        borderColor: theme.palette.divider,
        transition: 'all 0.2s ease-in-out',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          // backgroundColor: theme.palette.background.default,
          // borderBottom: '1px solid',
          // borderColor: theme.palette.divider,
        }}
      >
        <Typography variant="body1" sx={{ fontWeight: 600 }}>
          {day}
        </Typography>
        <CustomSwitch
          checked={isDayEnabled}
          onChange={() => handleToggleDay(!isDayEnabled)}
          inputProps={{ 'aria-label': `${day} working hours toggle` }}
        />
      </Box>

      <Collapse in={isDayEnabled}>
        {timeSlot.map((slot, index) => (
          <Box
            key={index}
            sx={{
              px: 2,
              py: 2,
              borderRadius: 1,
              border: '1px solid',
              borderColor: theme.palette.divider,
              backgroundColor: theme.palette.background.default,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Grid container spacing={3} sx={{ flex: 1 }}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Start Time</InputLabel>
                  <Select
                    value={slot.startTime}
                    label="Start Time"
                    onChange={e => handleStartTimeChange(index, e.target.value)}
                  >
                    {startTimeOptions.map(time => (
                      <MenuItem key={time} value={time}>
                        {time}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>End Time</InputLabel>
                  <Select
                    value={slot.endTime}
                    label="End Time"
                    onChange={e => onChange(day, index, { endTime: e.target.value })}
                  >
                    {startTimeOptions.map(time => (
                      <MenuItem key={time} value={time}>
                        {time}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {index === 0 ? (
              <IconButton
                onClick={handleAddSlot}
                aria-label="Add time slot"
                size="medium"
                sx={{
                  color: theme.palette.success.main,
                  ml: 2,
                }}
              >
                <IoIosAddCircleOutline />
              </IconButton>
            ) : (
              <IconButton
                onClick={() => handleDeleteSlot(index)}
                aria-label="Delete time slot"
                size="medium"
                sx={{
                  color: theme.palette.error.main,
                  ml: 2,
                }}
              >
                <MdDelete />
              </IconButton>
            )}
          </Box>
        ))}
      </Collapse>
    </Box>
  );
};

export default DayTimeSlot;
